import cls from 'clsx';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { Span } from '@/components/atoms/Typography/Typography';
import nameSpacesConfig from '@/config/config.nameSpaces.json';
import { FormConnector } from '@/containers/ConfigurePage/components/FormConfig/FormConfig';
import { useData } from '@/lib/pageData/pageDataContext';
import { TransformedAssetCategoryResult } from '@/types/TransformedAssetCategoryResults';
import {
  SubscriptionType,
  subscriptionCategories,
} from '@/utils/fetch/fetchProductSubscriptionByRegion';
import { formatPrice } from '@/utils/formatPrice';
import { useStudentDiscount } from '../../hooks/useStudentDiscount';
import {
  Membership,
  useMemberships,
} from '../ChooseMembershipThreeTier/hooks/useMemberships';
import styles from './Footnote.module.scss';

interface Props {
  flexibleMembership?: Membership;
  subscriptions: SubscriptionType[];
  product: TransformedAssetCategoryResult;
}

export const Footnote = ({ product, subscriptions }: Props) => {
  const { locale } = useRouter();
  const { currency } = useData<'default'>();
  const { t } = useTranslation([
    nameSpacesConfig.common,
    nameSpacesConfig.pages,
  ]);
  const memberships = useMemberships(subscriptions);
  const discount = useStudentDiscount(product, subscriptions);

  const flexibleMembership = memberships.find(
    item => item.durationMonths === '1'
  );

  const getPrice = (heavyUse: boolean, student: boolean) => {
    if (heavyUse && flexibleMembership?.rates?.normal) {
      const normalSubs = subscriptions.filter(
        sub =>
          sub.customerType === subscriptionCategories.normal &&
          sub.minDurationMonths === 1
      );
      const normalSub =
        normalSubs.find(sub => sub.isSpecialOffer) ?? normalSubs[0];

      const flexHeavyUseSubs = subscriptions.filter(
        sub =>
          sub.customerType === subscriptionCategories.heavyUse &&
          sub.minDurationMonths === 1
      );
      const flexHeavyUseSub =
        flexHeavyUseSubs.find(sub => sub.isSpecialOffer) ?? flexHeavyUseSubs[0];

      const additionalAmount =
        (flexHeavyUseSub.rate * 10 - normalSub.rate * 10) / 10;

      return flexibleMembership.rates?.normal + additionalAmount;
    }

    if (student && flexibleMembership?.rates?.normal && discount) {
      return (
        flexibleMembership.rates?.normal -
        (discount.normalRate - discount.studentRate)
      );
    }

    return flexibleMembership?.rates.normal;
  };

  return (
    <FormConnector>
      {({ durationMonths, heavyUse, student }) => {
        return (
          <Span
            version="body3"
            className={cls(styles.sixTwelveMonthsConditionsFootnote, {
              // TODO: figure out why form "jumps" if we remove the text
              [styles.hiddenContent]: Number(durationMonths) === 1,
            })}
          >
            {t('pages:configure.footnote.conditions', {
              price: formatPrice(
                getPrice(heavyUse === 'yes', student === 'yes'),
                locale!,
                currency
              ),
              durationMonths,
            })}
          </Span>
        );
      }}
    </FormConnector>
  );
};
